import type { OutboxRecord, OutboxStore } from './store';

/**
 * A persistent retry queue (WS-20; factored out of the check-in module's original
 * `outbox/queue.ts` verbatim — this class was already fully generic, so pulling it out
 * unchanged is what "reuse rather than fork" means here).
 *
 * The whole point of an outbox is that it survives things: a closed tab, a dead battery, a
 * week in a canyon with no signal. So *every* piece of retry state — attempt count and next
 * attempt time — lives in the record, not in memory. Starting a queue over an existing
 * store resumes exactly where the last session left off, with the backoff it had earned.
 *
 * The clock, the timer and the work itself are all injected, which is what makes the
 * reload-resume and backoff behaviour testable with fake timers and no network.
 */

/** What a handler decides about a record. */
export type AttemptOutcome =
  /** Delivered — remove it. */
  | 'done'
  /** Transient (offline, 5xx, timeout) — back off and try again, forever if need be. */
  | 'retry'
  /** Permanently rejected (400/413/415) — retrying can only fail identically. */
  | 'drop';

export interface RetryQueueOptions<T extends OutboxRecord> {
  store: OutboxStore<T>;
  attempt: (record: T) => Promise<AttemptOutcome>;
  now?: () => number;
  /** Called after any change so the UI can re-render queued badges. */
  onChange?: () => void;
  /** First retry delay; doubles per attempt up to `maxDelayMs`. */
  baseDelayMs?: number;
  maxDelayMs?: number;
  setTimeoutImpl?: (handler: () => void, ms: number) => ReturnType<typeof setTimeout>;
  clearTimeoutImpl?: (handle: ReturnType<typeof setTimeout>) => void;
}

const DEFAULT_BASE_DELAY_MS = 2_000;
const DEFAULT_MAX_DELAY_MS = 5 * 60 * 1000;

export class RetryQueue<T extends OutboxRecord> {
  private readonly store: OutboxStore<T>;
  private readonly attempt: (record: T) => Promise<AttemptOutcome>;
  private readonly now: () => number;
  private readonly onChange: () => void;
  private readonly baseDelayMs: number;
  private readonly maxDelayMs: number;
  private readonly setTimeoutImpl: NonNullable<RetryQueueOptions<T>['setTimeoutImpl']>;
  private readonly clearTimeoutImpl: NonNullable<RetryQueueOptions<T>['clearTimeoutImpl']>;

  private timer: ReturnType<typeof setTimeout> | null = null;
  private running = false;
  private draining: Promise<void> | null = null;

  constructor(options: RetryQueueOptions<T>) {
    this.store = options.store;
    this.attempt = options.attempt;
    this.now = options.now ?? (() => Date.now());
    this.onChange = options.onChange ?? ((): void => {});
    this.baseDelayMs = options.baseDelayMs ?? DEFAULT_BASE_DELAY_MS;
    this.maxDelayMs = options.maxDelayMs ?? DEFAULT_MAX_DELAY_MS;
    this.setTimeoutImpl = options.setTimeoutImpl ?? ((handler, ms) => setTimeout(handler, ms));
    this.clearTimeoutImpl = options.clearTimeoutImpl ?? ((handle) => clearTimeout(handle));
  }

  /** Exponential backoff, capped. Attempt 1 waits `base`, attempt 2 `2×base`, and so on. */
  backoffFor(attempts: number): number {
    return Math.min(this.baseDelayMs * 2 ** Math.max(0, attempts - 1), this.maxDelayMs);
  }

  async enqueue(
    record: Omit<T, 'attempts' | 'nextAttemptAt'> & Partial<OutboxRecord>,
  ): Promise<void> {
    await this.store.put({
      ...record,
      attempts: record.attempts ?? 0,
      nextAttemptAt: record.nextAttemptAt ?? this.now(),
    } as T);
    this.onChange();
    if (this.running) void this.drain();
  }

  async pending(): Promise<T[]> {
    return this.store.all();
  }

  /**
   * Begins processing. Called on boot *before* anything is enqueued, so a photo left over
   * from a previous session goes out without the user opening the composer again.
   */
  start(): void {
    if (this.running) return;
    this.running = true;
    void this.drain();
  }

  stop(): void {
    this.running = false;
    if (this.timer !== null) {
      this.clearTimeoutImpl(this.timer);
      this.timer = null;
    }
  }

  /** Try everything that is due right now, then schedule the next wake-up. */
  async drain(): Promise<void> {
    // One drain at a time; a concurrent call joins the one in flight.
    this.draining ??= this.drainOnce().finally(() => {
      this.draining = null;
    });
    return this.draining;
  }

  private async drainOnce(): Promise<void> {
    const records = await this.store.all();
    let changed = false;

    for (const record of records) {
      if (record.nextAttemptAt > this.now()) continue;

      let outcome: AttemptOutcome;
      try {
        outcome = await this.attempt(record);
      } catch {
        outcome = 'retry';
      }

      if (outcome === 'done' || outcome === 'drop') {
        await this.store.delete(record.id);
      } else {
        const attempts = record.attempts + 1;
        await this.store.put({
          ...record,
          attempts,
          nextAttemptAt: this.now() + this.backoffFor(attempts),
        });
      }
      changed = true;
    }

    if (changed) this.onChange();
    this.schedule(await this.store.all());
  }

  /** Sleep until the earliest due record rather than polling on a fixed tick. */
  private schedule(records: T[]): void {
    if (this.timer !== null) {
      this.clearTimeoutImpl(this.timer);
      this.timer = null;
    }
    if (!this.running || records.length === 0) return;

    const earliest = Math.min(...records.map((record) => record.nextAttemptAt));
    const delay = Math.max(0, earliest - this.now());
    this.timer = this.setTimeoutImpl(() => {
      this.timer = null;
      if (this.running) void this.drain();
    }, delay);
  }
}
